import React, {
  useState,
  forwardRef,
  useEffect,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { makeStyles, lighten } from "@material-ui/core/styles";
import { useSelector } from "react-redux";

import Button from "@material-ui/core/Button";
import TextField, { type TextFieldProps } from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";

import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import {
  endOfCalendarDayContainingInstant,
  startOfCalendarDayContainingInstant,
  zonedCurrentPresetPeriod,
} from "src/utils/timezone";

import type { Theme } from "src/theme";

interface TimePeriod {
  start: Date;
  end: Date;
}
interface CustomDatePickersProps {
  value: TimePeriod;
  onChange: (f: TimePeriod) => void;
}

const useCustomDatePickersStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    keyboardDatePicker: {
      "& input": {
        width: "6em",
      },
      margin: theme.spacing(2, 1, 1, 0),
    },
    inputRoot: {
      "& fieldset > legend > span": {
        display: "none",
      },
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    inputInput: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    inputLabelRoot: {
      zIndex: 1100,
      transform: "translate(14px, 10px) scale(1) !important",
    },
    inputLabelShrink: {
      transform: "translate(14px, -16px) scale(0.75) !important",
      color: `${
        isDark ? theme.palette.common.white : theme.palette.text.secondary
      } !important`,
    },
  };
});

function CustomDatePickers({ value, onChange }: CustomDatePickersProps) {
  const classes = useCustomDatePickersStyles();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const [period, setPeriod] = useState({
    start: value.start,
    end: value.end,
  });

  useEffect(() => {
    setPeriod({ start: value.start, end: value.end });
  }, [value.start, value.end]);

  return (
    <>
      <ZonedKeyboardDatePicker
        disableToolbar
        className={classes.keyboardDatePicker}
        InputProps={{
          classes: {
            root: classes.inputRoot,
            input: classes.inputInput,
          },
        }}
        InputLabelProps={{
          classes: {
            root: classes.inputLabelRoot,
            shrink: classes.inputLabelShrink,
          },
        }}
        inputVariant="outlined"
        variant="inline"
        format="MM/dd/yyyy"
        margin="dense"
        label="Start Date"
        autoOk={true}
        maxDate={period.end || value.end}
        value={period.start || null}
        onChange={(utcFromPicker) => {
          if (utcFromPicker == null) {
            return;
          }
          if (isNaN(utcFromPicker.getTime())) {
            setPeriod((p) => ({ ...p, start: utcFromPicker }));
            return;
          }
          const start = startOfCalendarDayContainingInstant(
            utcFromPicker,
            timeZone
          );
          setPeriod((p) => {
            const next = { ...p, start };
            onChange(next);
            return next;
          });
        }}
        KeyboardButtonProps={{
          "aria-label": "Start date",
        }}
      />
      <ZonedKeyboardDatePicker
        disableToolbar
        className={classes.keyboardDatePicker}
        InputProps={{
          classes: {
            root: classes.inputRoot,
            input: classes.inputInput,
          },
        }}
        InputLabelProps={{
          classes: {
            root: classes.inputLabelRoot,
            shrink: classes.inputLabelShrink,
          },
        }}
        inputVariant="outlined"
        variant="inline"
        format="MM/dd/yyyy"
        margin="dense"
        label="End Date"
        autoOk={true}
        minDate={period.start || value.start}
        value={period.end || null}
        onChange={(utcFromPicker) => {
          if (utcFromPicker == null) {
            return;
          }
          if (isNaN(utcFromPicker.getTime())) {
            setPeriod((p) => ({ ...p, end: utcFromPicker }));
            return;
          }
          const end = endOfCalendarDayContainingInstant(
            utcFromPicker,
            timeZone
          );
          setPeriod((p) => {
            const next = { ...p, end };
            onChange(next);
            return next;
          });
        }}
        KeyboardButtonProps={{
          "aria-label": "End date",
        }}
      />
    </>
  );
}

const useSelectStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      "& input": {
        width: "6em",
      },
      margin: theme.spacing(2, 1, 1, 0),
      minWidth: "150px",
    },
    inputRoot: {
      "& fieldset > legend > span": {
        display: "none",
      },
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: isDark
          ? "0 2px 4px rgba(255,255,255,0.08)"
          : "0 2px 4px rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
      },
    },
    inputInput: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    inputLabelRoot: {
      zIndex: 1100,
      transform: "translate(14px, 10px) scale(1) !important",
    },
    inputLabelShrink: {
      transform: "translate(14px, -16px) scale(0.75) !important",
      color: `${
        isDark ? theme.palette.common.white : theme.palette.text.secondary
      } !important`,
    },
  };
});

export function Select({ className, ...props }: TextFieldProps) {
  const classes = useSelectStyles();
  return (
    <TextField
      variant="outlined"
      select
      className={clsx(classes.root, className)}
      InputProps={{
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput,
        },
      }}
      InputLabelProps={{
        classes: {
          root: classes.inputLabelRoot,
          shrink: classes.inputLabelShrink,
        },
      }}
      {...props}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === "dark";
  const { custom: palette } = theme.palette.custom;

  return {
    root: {
      display: "flex",
      margin: theme.spacing(0, 0, 2, 0),
      alignItems: "center",
      flexWrap: "wrap",
    },
    button: {
      whiteSpace: "nowrap",
      margin: theme.spacing(2, 1, 1, 0),
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
      transition:
        "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        boxShadow: isDark
          ? "0 2px 4px rgba(255,255,255,0.08)"
          : "0 2px 4px rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
      },
    },
    active: {
      "&:hover": {
        backgroundColor: lighten(palette.brandSecondary, 0.2),
      },
      color: theme.palette.getContrastText(palette.brandSecondary),
      backgroundColor: palette.brandSecondary,
    },
  };
});

interface PresetTimeOption {
  type: "day" | "week" | "month" | "year";
}

interface CustomTimeOption {
  type: "custom";
  period: TimePeriod;
}

export type TimeOption = PresetTimeOption | CustomTimeOption;

export function getPeriodFromPreset(
  preset: PresetTimeOption,
  timeZone: string
): TimePeriod {
  return zonedCurrentPresetPeriod(preset.type, timeZone);
}

const Entries: { value: TimeOption; text: string }[] = [
  { value: { type: "day" }, text: "Current day" },
  { value: { type: "week" }, text: "Current week" },
  { value: { type: "month" }, text: "Current month" },
  { value: { type: "year" }, text: "Current year" },
];

const TimeFilter = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    value: TimeOption;
    onChange: (v: TimeOption) => void;
    disabled?: boolean;
    endChildren?: ReactNode;
    tooltip?: string;
    noTime?: boolean;
  }>
>(
  (
    { value, onChange, children, disabled, endChildren, tooltip, noTime },
    ref
  ) => {
    const classes = useStyles();
    const timeZone = useSelector(effectiveUserTimeZoneSelector);
    let buttons: ReactNode = noTime
      ? null
      : [
          ...Entries.map((entry) => (
            <Button
              key={entry.value.type}
              onClick={() => onChange(entry.value)}
              className={clsx(
                classes.button,
                !disabled && value.type === entry.value.type && classes.active
              )}
              variant="contained"
              disabled={disabled}
            >
              {entry.text}
            </Button>
          )),
          <Button
            key="custom"
            className={clsx(
              classes.button,
              !disabled && value.type === "custom" && classes.active
            )}
            variant="contained"
            onClick={() =>
              onChange(
                value.type === "custom"
                  ? value
                  : {
                      type: "custom",
                      period: getPeriodFromPreset(value, timeZone),
                    }
              )
            }
            disabled={disabled}
          >
            Custom
          </Button>,
        ];

    if (buttons && tooltip)
      buttons = (
        <Tooltip title={tooltip}>
          <div>{buttons}</div>
        </Tooltip>
      );

    return (
      <div
        className={classes.root}
        ref={ref}
        id={"transaction-details-time-select"}
      >
        {children}
        {buttons}
        {!disabled && !noTime && value.type === "custom" && (
          <CustomDatePickers
            value={value.period}
            onChange={(period) => onChange({ type: "custom", period })}
          />
        )}
        {endChildren || null}
      </div>
    );
  }
);

export default TimeFilter;
